const fractionCharacters = {
  '¼': 0.25,
  '½': 0.5,
  '¾': 0.75,
  '⅓': 1 / 3,
  '⅔': 2 / 3,
  '⅛': 0.125,
  '⅜': 0.375,
  '⅝': 0.625,
  '⅞': 0.875
}

const pluralize = (word, amount) => {
  if (amount === 1 || /s$/i.test(word)) return word
  return `${word}s`
}

export function parseQuantity(value) {
  if (typeof value !== 'string') return null

  const normalized = value.trim().replace(/\\/g, '/')
  const mixedFraction = normalized.match(/^(\d+(?:\.\d+)?)\s+(\d+)\s*\/\s*(\d+)/)
  if (mixedFraction) {
    return Number(mixedFraction[1]) + Number(mixedFraction[2]) / Number(mixedFraction[3])
  }

  const fraction = normalized.match(/^(\d+)\s*\/\s*(\d+)/)
  if (fraction) return Number(fraction[1]) / Number(fraction[2])

  const unicodeFraction = normalized.match(/^(\d+)?\s*([¼½¾⅓⅔⅛⅜⅝⅞])/)
  if (unicodeFraction) {
    return Number(unicodeFraction[1] || 0) + fractionCharacters[unicodeFraction[2]]
  }

  const decimal = normalized.match(/^\d+(?:\.\d+)?/)
  return decimal ? Number(decimal[0]) : null
}

export function getRecipeYield(recipe) {
  const recipeText = [
    ...(Array.isArray(recipe?.ingredients) ? recipe.ingredients : []),
    recipe?.instructions || ''
  ]

  for (const text of recipeText) {
    if (typeof text !== 'string') continue

    const match = text.match(/\b(serves?|makes?)\s*:?\s*(?:about\s+)?((?:\d+(?:\.\d+)?\s+)?\d+(?:\s*[\\/]\s*\d+)?)\s*([a-z]+)?/i)
    if (!match || /^\s*(?:[-=]|or\b|to\b)/i.test(text.slice(match.index + match[0].length))) continue

    const amount = parseQuantity(match[2])
    if (!Number.isFinite(amount) || amount <= 0) continue

    const isServes = /^serves?$/i.test(match[1])
    const unit = isServes ? 'serving' : (match[3] || 'batch')

    return {
      amount,
      unit: pluralize(unit, amount),
      label: `${amount} ${pluralize(unit, amount)}`
    }
  }

  return null
}

const formatFraction = (value) => {
  const sixteenths = Math.round(value * 16)
  const whole = Math.floor(sixteenths / 16)
  const remainder = sixteenths % 16

  if (!remainder) return String(whole)

  const divisor = remainder % 8 === 0 ? 2 : remainder % 4 === 0 ? 4 : remainder % 2 === 0 ? 8 : 16
  const numerator = remainder / (16 / divisor)
  return whole ? `${whole} ${numerator}/${divisor}` : `${numerator}/${divisor}`
}

const roundForKitchen = (amount) => {
  const increment = amount >= 10 ? 0.5 : amount >= 1 ? 0.125 : 0.0625
  return Math.round(amount / increment) * increment
}

export function scaleIngredient(ingredient, multiplier) {
  if (typeof ingredient !== 'string' || !Number.isFinite(multiplier)) return ingredient

  const normalized = ingredient.replace(/\\/g, '/')
  const numericMatch = normalized.match(/^(?:(\d+(?:\.\d+)?)\s+(\d+)\s*\/\s*(\d+)|(\d+)\s*\/\s*(\d+)|(\d+(?:\.\d+)?))/)
  if (!numericMatch) return ingredient

  const originalAmount = numericMatch[1]
    ? Number(numericMatch[1]) + Number(numericMatch[2]) / Number(numericMatch[3])
    : numericMatch[4]
      ? Number(numericMatch[4]) / Number(numericMatch[5])
      : Number(numericMatch[6])

  if (!Number.isFinite(originalAmount)) return ingredient

  const scaledAmount = roundForKitchen(originalAmount * multiplier)
  return `${formatFraction(scaledAmount)}${ingredient.slice(numericMatch[0].length)}`
}
